import type { AuthContextProps } from 'react-oidc-context';
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { UpdateCommand, DynamoDBDocumentClient, QueryCommand } from "@aws-sdk/lib-dynamodb";
import type { Serializer } from '../serializers/Serializer';
import type { Parser } from '../parsers/Parser';

export class DatabaseStorage<T extends object> {
    private table: string;
    private partition_key: string;
    private sort_key: string;
    private client: DynamoDBClient;
    private docClient: DynamoDBDocumentClient;
    private serializer: Serializer<T>;
    private parser: Parser<T>;

    public constructor(table: string, partition_key: string, sort_key: string, serializer: Serializer<T>, parser: Parser<T>, auth: AuthContextProps) {
        this.table = table;
        this.partition_key = partition_key;
        this.sort_key = sort_key;
        this.serializer = serializer;
        this.parser = parser;
        this.client = new DynamoDBClient({
            region: import.meta.env.VITE_DYNAMODB_REGION,
            credentials: fromCognitoIdentityPool({
                clientConfig: { region: import.meta.env.VITE_COGNITO_REGION },
                identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
                logins: auth.user?.id_token ? {
                            [import.meta.env.VITE_COGNITO_IDP]: auth.user.id_token
                        } : undefined
                    })
                });
        this.docClient = DynamoDBDocumentClient.from(this.client);
    }
    public async append(value: T, partition_key_value: string, sort_key_value: string): Promise<void> {
        const serialized = this.serializer.serialize(value as T extends object ? T : never);
        const params = {
            TableName: this.table,
            Key: {
                [this.partition_key]: partition_key_value,
                [this.sort_key]: sort_key_value
            },
            UpdateExpression: 'SET #value = list_append(if_not_exists(#value, :empty_list), :new_value)',
            ExpressionAttributeNames: {
                '#value': 'value'
            },
            ExpressionAttributeValues: {
                ':new_value': [serialized],
                ':empty_list': []
            }
        };
        const command = new UpdateCommand(params);
        try {
            await this.docClient.send(command);
        } catch (error) {
            console.error(`Error sending data to DynamoDB table '${this.table}':`, error);
            throw error;
        }
    }

    public async get(partition_key_value: string, sort_key_value: string): Promise<T[]> {
        const params = {
            TableName: this.table,
            KeyConditionExpression: `${this.partition_key} = :partition_key AND ${this.sort_key} = :sort_key`,
            ExpressionAttributeValues: {
                ':partition_key': partition_key_value,
                ':sort_key': sort_key_value
            }
        };
        let raw;
        try {
            raw = await this.docClient.send(new QueryCommand(params));
        } catch (error) {
            console.error(`Error querying data from DynamoDB table '${this.table}':`, error);
            throw error;
        }
        try {
            const values = raw.Items?.reduceRight((acc: T[], item: object) => {
                const value_list = item && 'value' in item ? item.value : undefined;
                if (value_list === undefined || value_list === null) {
                    throw new Error('value is undefined or null');
                }
                if (!Array.isArray(value_list)) {
                    return acc.concat(this.parser.parse(value_list));
                }
                return acc.concat(value_list.map((value) => this.parser.parse(value)));
            }, []) || [] as T[];
            return values;
        } catch {
            console.error(`Error parsing data from DynamoDB table '${this.table}'`);
            return [] as T[];
        }
    }

    public remove(key: string): void {
        localStorage.removeItem(key);
    }

    public clear(): void {
        localStorage.clear();
    }
}